import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { App, ConfigProvider, Modal, Segmented } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import zhCN from 'antd/locale/zh_CN';
import Plans from '@/pages/payment/Plans';
import Recharge from '@/pages/payment/Recharge';
import Checkout from '@/pages/payment/Checkout';
import { createStyles } from 'antd-style';

type TabKey = 'plans' | 'recharge';
type ViewKey = 'select' | 'checkout';

type ShowOptions = {
  defaultTab?: TabKey;
};

let root: Root | null = null;
let container: HTMLDivElement | null = null;
let currentVisible = false;
let currentOptions: ShowOptions = {};

const useStyles = createStyles(({ token, css }) => ({
  modal: css`
    .ant-modal-content {
      border-radius: 18px;
      overflow: visible;
    }
    .ant-modal-header {
      border-top-left-radius: 18px;
      border-top-right-radius: 18px;
    }
  `,
  modalWrap: css`
    position: relative;
  `,
  closeBtn: css`
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(50%, -50%);
    width: 40px;
    height: 40px;
    border-radius: 999px;
    background: ${token.colorBgElevated};
    border: 1px solid ${token.colorBorderSecondary};
    box-shadow: 0 18px 34px -20px rgba(0, 0, 0, 0.55);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 3;
    transition: transform 160ms ease, border-color 160ms ease;
  `,
  closeBtnHover: css`
    &:hover {
      transform: translate(50%, -50%) scale(1.02);
      border-color: ${token.colorPrimaryBorder};
    }
  `,
}));

function getContainer() {
  if (!container) {
    container = document.createElement('div');
    container.id = 'global-payment-modal';
    document.body.appendChild(container);
  }
  return container;
}

function PaymentModalContent(props: { open: boolean; onClose: () => void; options: ShowOptions }) {
  const { open, onClose, options } = props;
  const { styles } = useStyles();
  const [tab, setTab] = React.useState<TabKey>(options.defaultTab || 'plans');
  const [view, setView] = React.useState<ViewKey>('select');
  const [checkoutCtx, setCheckoutCtx] = React.useState<any>(null);

  React.useEffect(() => {
    setTab(options.defaultTab || 'plans');
    setView('select');
    setCheckoutCtx(null);
  }, [options.defaultTab]);

  const startCheckout = (ctx: any) => {
    setCheckoutCtx(ctx);
    setView('checkout');
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      width={tab === 'recharge' ? 'min(920px, calc(100vw - 24px))' : 'min(1240px, calc(100vw - 24px))'}
      className={styles.modal}
      closable={false}
      title={
        view === 'select' ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontWeight: 800 }}>订阅与充值</div>
            <Segmented
              size="middle"
              value={tab}
              onChange={(v) => setTab(v as TabKey)}
              options={[
                { label: '订阅', value: 'plans', disabled: false },
                { label: '充值', value: 'recharge', disabled: false },
              ]}
            />
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontWeight: 800 }}>确认支付</div>
            <button
              type="button"
              onClick={() => {
                setView('select');
                setCheckoutCtx(null);
              }}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: 0,
                color: 'rgba(0,0,0,0.45)',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              返回
            </button>
          </div>
        )
      }
      styles={{
        body: {
          padding: 16,
          maxHeight: '84vh',
          overflow: 'auto',
        },
      }}
      modalRender={(node) => (
        <div className={styles.modalWrap}>
          {node}
          <button
            type="button"
            aria-label="关闭"
            className={`${styles.closeBtn} ${styles.closeBtnHover}`}
            onClick={onClose}
          >
            <CloseOutlined />
          </button>
        </div>
      )}
    >
      {view === 'select' ? (
        tab === 'plans' ? (
          <Plans embedded onStartCheckout={startCheckout} />
        ) : (
          <Recharge embedded onStartCheckout={startCheckout} />
        )
      ) : (
        <Checkout embedded ctx={checkoutCtx} onDone={onClose} />
      )}
    </Modal>
  );
}

function render(visible: boolean, options: ShowOptions = {}) {
  currentVisible = visible;
  currentOptions = options;

  const containerEl = getContainer();
  if (!root) {
    root = createRoot(containerEl);
  }

  const onClose = () => render(false, currentOptions);

  root.render(
    <ConfigProvider locale={zhCN}>
      <App>
        <PaymentModalContent open={visible} onClose={onClose} options={options} />
      </App>
    </ConfigProvider>,
  );
}

export const paymentModal = {
  show(options?: ShowOptions) {
    render(true, options || {});
  },
  hide() {
    render(false, currentOptions);
  },
  isVisible() {
    return currentVisible;
  },
};
